import { Role, type UpdateFrequency } from '@kanbu/schema/enums';
import { Trans, useLingui } from '@lingui/react/macro';
import * as Tabs from '@radix-ui/react-tabs';
import {
  Button,
  Dialog,
  Input,
  Label,
  Select,
  SelectItem,
  toast,
} from '@utima/ui';
import { Info } from 'lucide-react';
import { memo, useState } from 'react';

import { LockedTabLabel } from '@/components';
import { SimpleTooltip } from '@/components/tooltips/simple-tooltip';
import { AgentAccessSelector } from '@/features/documents/components/AgentAccessSelector';
import { formatAutoUpdateCosts } from '@/features/documents/lib/autoUpdateCostCopy';
import { UPDATE_FREQUENCY_OPTIONS } from '@/features/documents/lib/updateFrequencyOptions';
import { useEnumTranslations } from '@/hooks';
import { useAccess } from '@/hooks/useAccess';
import { useCurrentOrganization } from '@/hooks/useCurrentOrganization';
import { useSubscriptionFeatures } from '@/hooks/useSubscriptionFeatures';
import { trpc } from '@/services/trpc';

import { FolderDefaultPriorityHint } from './FolderDefaultPriorityHint';
import { PrioritySelect } from './PrioritySelect';
import type { useKBMutations } from '../hooks/useKBMutations';

export type CreateFolderModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  parentId: string | null;
  mutations: ReturnType<typeof useKBMutations>;
  /** When set, auto-preselects this chat in agent access. */
  chatId?: string;
};

const INHERIT_VALUE = '__inherit__';

const CHANGE_THRESHOLD_OPTIONS = [
  { value: INHERIT_VALUE, label: 'Inherit' },
  { value: '0.01', label: '1%' },
  { value: '0.05', label: '5%' },
  { value: '0.1', label: '10%' },
  { value: '0.2', label: '20%' },
];

export const CreateFolderModal = memo(function CreateFolderModal({
  open,
  onOpenChange,
  parentId,
  mutations,
  chatId,
}: CreateFolderModalProps) {
  const { t } = useLingui();
  const enumTranslations = useEnumTranslations();
  const { organization } = useCurrentOrganization();
  const { check: checkCost, update: updateCost } = formatAutoUpdateCosts(
    organization?.currency,
  );
  const { hasAccess } = useAccess();
  const isSuperAdmin = hasAccess([Role.SuperAdmin]);
  const { canUseKbAutoUpdates, isKbAutoUpdatesLocked } =
    useSubscriptionFeatures();
  const [activeTab, setActiveTab] = useState('general');

  // General tab state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [defaultWeight, setDefaultWeight] = useState('1');

  // Auto-update tab state
  const [updateFrequency, setUpdateFrequency] = useState<string>(INHERIT_VALUE);
  const [changeThreshold, setChangeThreshold] = useState<string>(INHERIT_VALUE);

  // Agent access tab state
  const [availableForAllAgents, setAvailableForAllAgents] = useState(false);
  const [selectedChatIds, setSelectedChatIds] = useState<string[]>(
    chatId ? [chatId] : [],
  );

  const { data: allChats } = trpc.chats.findAll.useQuery(
    { pagination: { pageSize: 100 }, fields: ['id', 'name'] },
    { enabled: open },
  );

  const resetState = () => {
    setName('');
    setDescription('');
    setDefaultWeight('1');
    setUpdateFrequency(INHERIT_VALUE);
    setChangeThreshold(INHERIT_VALUE);
    setAvailableForAllAgents(false);
    setSelectedChatIds(chatId ? [chatId] : []);
    setActiveTab('general');
  };

  const handleSubmit = async () => {
    if (!name.trim()) return;

    try {
      const folder = await mutations.createFolder.mutateAsync({
        name: name.trim(),
        description: description.trim() || undefined,
        parentId,
        defaultWeight: Number.parseFloat(defaultWeight),
        availableForAllAgents,
        chatIds: availableForAllAgents ? [] : selectedChatIds,
      });

      // Apply auto-update settings if any were changed from defaults
      const hasAutoUpdateChanges =
        canUseKbAutoUpdates &&
        (updateFrequency !== INHERIT_VALUE ||
          (isSuperAdmin && changeThreshold !== INHERIT_VALUE));

      if (hasAutoUpdateChanges) {
        await mutations.updateFolder.mutateAsync({
          id: folder.id,
          updateFrequency:
            updateFrequency === INHERIT_VALUE
              ? null
              : (updateFrequency as UpdateFrequency) || null,
          changeThreshold:
            isSuperAdmin && changeThreshold !== INHERIT_VALUE
              ? Number.parseFloat(changeThreshold) || null
              : null,
        });
      }

      resetState();
      onOpenChange(false);
      toast.success(t`Folder created`);
    } catch {
      toast.error(t`Failed to create folder`);
    }
  };

  const tabTriggerClass =
    'px-4 py-2 text-sm font-medium data-[state=active]:border-b-2 data-[state=active]:border-primary';

  return (
    <Dialog.Root
      open={open}
      onOpenChange={isOpen => {
        if (isOpen) resetState();
        onOpenChange(isOpen);
      }}
    >
      <Dialog.Content className='w-[600px]'>
        <Dialog.Header>
          <Dialog.Title>
            <Trans>Create Folder</Trans>
          </Dialog.Title>
          <Dialog.Description>
            <Trans>Create a new folder to organize your documents.</Trans>
          </Dialog.Description>
        </Dialog.Header>

        <Tabs.Root
          value={activeTab}
          onValueChange={setActiveTab}
          className='flex min-h-0 flex-col'
        >
          <Tabs.List className='border-b border-border'>
            <div className='flex gap-2'>
              <Tabs.Trigger value='general' className={tabTriggerClass}>
                <Trans>General</Trans>
              </Tabs.Trigger>
              <Tabs.Trigger value='auto-update' className={tabTriggerClass}>
                <LockedTabLabel locked={isKbAutoUpdatesLocked}>
                  <Trans>Auto-Update Settings</Trans>
                </LockedTabLabel>
              </Tabs.Trigger>
              <Tabs.Trigger value='agents' className={tabTriggerClass}>
                <Trans>Agent Access</Trans>
              </Tabs.Trigger>
            </div>
          </Tabs.List>

          {/* General tab */}
          <Tabs.Content value='general' className='space-y-4 py-4'>
            <div className='space-y-1.5'>
              <Label htmlFor='folderName'>
                <Trans>Name</Trans>
              </Label>
              <Input
                id='folderName'
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder={t`Folder name`}
                autoFocus
                onKeyDown={e => {
                  if (e.key === 'Enter') handleSubmit();
                }}
              />
            </div>
            <div className='space-y-1.5'>
              <Label htmlFor='folderDescription'>
                <Trans>Description</Trans>
              </Label>
              <Input
                id='folderDescription'
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder={t`Optional description`}
              />
            </div>
            <div className='space-y-1.5'>
              <Label htmlFor='defaultWeight'>
                <Trans>Default priority</Trans>
              </Label>
              <PrioritySelect
                id='defaultWeight'
                value={defaultWeight}
                onValueChange={setDefaultWeight}
              />
              <FolderDefaultPriorityHint />
            </div>
          </Tabs.Content>

          <Tabs.Content value='auto-update' className='space-y-4 py-4'>
            <fieldset
              disabled={isKbAutoUpdatesLocked}
              className='space-y-4 disabled:pointer-events-none disabled:opacity-50'
            >
              <div className='space-y-1.5'>
                <div className='flex items-center gap-1.5'>
                  <Label>
                    <Trans>Keep included pages updated</Trans>
                  </Label>
                  <SimpleTooltip
                    title={t`How often to re-check content of pages already in the knowledge base. About ${checkCost} per page check, up to ${updateCost} if the page actually changes.`}
                  >
                    <Info className='size-3.5 shrink-0 cursor-help text-muted-fg' />
                  </SimpleTooltip>
                </div>
                <Select
                  value={updateFrequency}
                  onValueChange={setUpdateFrequency}
                >
                  <SelectItem value={INHERIT_VALUE}>
                    <Trans>Inherit</Trans>
                  </SelectItem>
                  {UPDATE_FREQUENCY_OPTIONS.map(freq => (
                    <SelectItem key={freq} value={freq}>
                      {enumTranslations.updateFrequency[freq]}
                    </SelectItem>
                  ))}
                </Select>
              </div>

              {isSuperAdmin && (
                <div className='space-y-1.5'>
                  <div className='flex items-center gap-1.5'>
                    <Label>
                      <Trans>Change Threshold</Trans>
                    </Label>
                    <SimpleTooltip
                      title={t`Minimum content change percentage to trigger re-embedding.`}
                    >
                      <Info className='size-3.5 shrink-0 cursor-help text-muted-fg' />
                    </SimpleTooltip>
                  </div>
                  <Select
                    value={changeThreshold}
                    onValueChange={setChangeThreshold}
                  >
                    {CHANGE_THRESHOLD_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.value === INHERIT_VALUE ? t`Inherit` : opt.label}
                      </SelectItem>
                    ))}
                  </Select>
                </div>
              )}
            </fieldset>
          </Tabs.Content>

          {/* Agent Access tab */}
          <Tabs.Content value='agents' className='space-y-4 py-4'>
            <p className='text-sm text-muted-fg'>
              <Trans>
                Choose which agents can access documents in this folder.
              </Trans>
            </p>
            {allChats?.items && (
              <AgentAccessSelector
                availableForAllAgents={availableForAllAgents}
                setAvailableForAllAgents={setAvailableForAllAgents}
                selectedChatIds={selectedChatIds}
                setSelectedChatIds={setSelectedChatIds}
                availableAgents={allChats.items}
                currentChatId={chatId}
              />
            )}
          </Tabs.Content>
        </Tabs.Root>

        <Dialog.Footer>
          <Button variant='ghost' outline onClick={() => onOpenChange(false)}>
            <Trans>Cancel</Trans>
          </Button>
          <Button
            onClick={handleSubmit}
            loading={
              mutations.createFolder.isPending ||
              mutations.updateFolder.isPending
            }
            disabled={!name.trim()}
          >
            <Trans>Create</Trans>
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
});
